import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Check, X, ShieldCheck, CalendarDays, Users, Euro } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupplierAction() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | error | ready
  const [errMsg, setErrMsg] = useState("");
  const [req, setReq] = useState(null);
  const [busy, setBusy] = useState(null);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState("");

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/supplier-request/${token}`);
      setReq(data);
      setState("ready");
    } catch (e) {
      setErrMsg(e?.response?.data?.detail || "Poveznica nije valjana.");
      setState("error");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const accept = async () => {
    setBusy("accept");
    try {
      const { data } = await axios.post(`${API}/supplier-request/${token}/accept`);
      toast.success(`Prihvaćeno — klijent ima ${data.hours || 24}h za uplatu akontacije`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Nije uspjelo."); }
    finally { setBusy(null); }
  };

  const decline = async () => {
    setBusy("decline");
    try {
      await axios.post(`${API}/supplier-request/${token}/decline`, { reason });
      toast.success("Zahtjev odbijen — klijent je obaviješten");
      setDeclining(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Nije uspjelo."); }
    finally { setBusy(null); }
  };

  const STATUS_VIEW = {
    accepted: { icon: <ShieldCheck className="w-10 h-10 text-emerald-300 mx-auto mb-3" />, cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200", text: "Zahtjev je PRIHVAĆEN. Klijent je dobio poveznicu za uplatu akontacije." },
    confirmed: { icon: <ShieldCheck className="w-10 h-10 text-emerald-300 mx-auto mb-3" />, cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200", text: "Rezervacija je POTVRĐENA I ZAKLJUČANA — akontacija je uplaćena. Voucher je poslan e-mailom." },
    declined: { icon: <X className="w-10 h-10 text-red-300 mx-auto mb-3" />, cls: "border-red-500/40 bg-red-500/10 text-red-200", text: "Zahtjev je ODBIJEN. Klijent je obaviješten." },
    expired: { icon: <X className="w-10 h-10 text-amber-300 mx-auto mb-3" />, cls: "border-amber-500/40 bg-amber-500/10 text-amber-200", text: "Rok za uplatu akontacije je istekao." },
    cancelled: { icon: <X className="w-10 h-10 text-zinc-400 mx-auto mb-3" />, cls: "border-zinc-500/40 bg-zinc-500/10 text-zinc-300", text: "Zahtjev je otkazan." },
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white py-10 px-4">
      <div className="max-w-xl mx-auto">
        <p className="text-[10px] uppercase tracking-[0.4em] text-[#C5A059] mb-1">Juke Transfers · Supplier</p>
        <h1 className="font-display text-2xl sm:text-3xl font-light mb-6">Zahtjev za rezervaciju</h1>

        {state === "loading" && (
          <div className="flex items-center gap-2 text-zinc-500 text-sm" data-testid="supplier-action-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Učitavanje…
          </div>
        )}
        {state === "error" && (
          <div data-testid="supplier-action-error" className="rounded-2xl border border-red-500/40 bg-red-500/10 p-6 text-sm text-red-200">{errMsg}</div>
        )}

        {state === "ready" && req && (
          <>
            <div data-testid="supplier-request-details" className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-6 space-y-3 mb-6">
              <p className="text-xs uppercase tracking-[0.25em] text-zinc-500">Referenca {req.ref}</p>
              <p className="text-xl text-white font-light">{req.service_title}</p>
              <div className="grid grid-cols-2 gap-3 text-sm text-zinc-300">
                <p className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-[#C5A059]" /> {req.service_date || "—"}{req.service_time ? ` · ${req.service_time}h` : ""}</p>
                <p className="flex items-center gap-2"><Users className="w-4 h-4 text-[#C5A059]" /> {req.guests || "—"} gostiju</p>
                <p className="flex items-center gap-2"><Euro className="w-4 h-4 text-[#C5A059]" /> Ukupno €{Number(req.total_price_eur || 0).toFixed(2)}</p>
                <p className="flex items-center gap-2 text-zinc-500 text-xs">Akontacija €{Number(req.deposit_eur || 0).toFixed(2)} (naplaćuje Juke)</p>
              </div>
              {req.notes && <p className="text-xs text-zinc-500 italic border-t border-white/5 pt-3">"{req.notes}"</p>}
            </div>

            {req.status === "pending_supplier" ? (
              <div className="space-y-3">
                <button data-testid="supplier-accept-btn" onClick={accept} disabled={!!busy}
                  className="w-full h-13 py-4 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-sm uppercase tracking-[0.2em] font-semibold disabled:opacity-50 flex items-center justify-center gap-2 transition-colors">
                  {busy === "accept" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Prihvati (PRIHVATI)
                </button>
                {!declining ? (
                  <button data-testid="supplier-decline-btn" onClick={() => setDeclining(true)} disabled={!!busy}
                    className="w-full h-13 py-4 rounded-full border border-red-500/50 text-red-300 hover:bg-red-500/10 text-sm uppercase tracking-[0.2em] font-semibold flex items-center justify-center gap-2 transition-colors">
                    <X className="w-4 h-4" /> Odbij (ODBIJI)
                  </button>
                ) : (
                  <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-4 space-y-3">
                    <textarea data-testid="decline-reason-input" value={reason} onChange={(e) => setReason(e.target.value)}
                      placeholder="Razlog (nije obavezno)…" rows={2}
                      className="w-full px-3 py-2 bg-[#050505] border border-white/10 rounded-lg text-sm outline-none focus:border-red-400" />
                    <div className="flex gap-2">
                      <button onClick={() => setDeclining(false)} className="flex-1 h-10 rounded-full border border-white/15 text-zinc-300 text-xs uppercase tracking-widest">Natrag</button>
                      <button data-testid="decline-confirm-btn" onClick={decline} disabled={!!busy}
                        className="flex-1 h-10 rounded-full bg-red-600 hover:bg-red-500 text-white text-xs uppercase tracking-widest font-semibold disabled:opacity-50 flex items-center justify-center gap-1">
                        {busy === "decline" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />} Potvrdi odbijanje
                      </button>
                    </div>
                  </div>
                )}
                <p className="text-[11px] text-zinc-500 text-center">Prihvatom se klijentu automatski šalje poveznica za uplatu akontacije.</p>
              </div>
            ) : (
              <div data-testid="supplier-action-status" className={`rounded-2xl border p-8 text-center ${STATUS_VIEW[req.status]?.cls || "border-white/10 text-zinc-300"}`}>
                {STATUS_VIEW[req.status]?.icon}
                <p className="text-sm">{STATUS_VIEW[req.status]?.text || req.status}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
